import { useReducer, useRef, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../config/supabase';
import { post, get, getErrorMessage } from '../utils/api';
import { useBattleRoom } from './useBattleRoom';
import { useAuth } from './useAuth';
import type {
  BattleGameState,
  BattleGameAction,
  BattleRoomState,
  BattlePlayerState,
} from '../types/battleRush';

const TICK_INTERVAL_MS = 100;
const ROUND_TIMER_MS = 10_000; // 10s client-side countdown (server has 15s)

const initialState: BattleGameState = {
  phase: 'joining',
  roomCode: null,
  dbRoomId: null,
  room: null,
  error: null,
  isLoading: false,
  timeRemaining: ROUND_TIMER_MS,
  timerDuration: ROUND_TIMER_MS,
};

function battleReducer(state: BattleGameState, action: BattleGameAction): BattleGameState {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, isLoading: true, error: null };

    case 'SET_ERROR':
      return { ...state, isLoading: false, error: action.error };

    case 'SET_DB_ROOM_ID':
      return { ...state, dbRoomId: action.dbRoomId };

    case 'JOINED_ROOM':
      return {
        ...state,
        phase: action.room.status === 'waiting' ? 'lobby' : action.room.status === 'finished' ? 'results' : 'playing',
        roomCode: action.room.roomCode,
        room: action.room,
        isLoading: false,
        error: null,
      };

    case 'ROOM_UPDATED': {
      if (!state.room) return state;

      // Guard: once game is finished, ignore stale non-finished updates
      // BUT allow 'waiting' through (host restarted the room)
      if (state.phase === 'results' && action.room.status && action.room.status !== 'finished' && action.room.status !== 'waiting') {
        return state;
      }

      const updatedRoom = { ...state.room, ...action.room };

      // Guard: preserve winnerUserId once set (stale polls may try to clear it)
      // BUT allow clearing when room resets to waiting
      if (state.room.winnerUserId && !updatedRoom.winnerUserId && updatedRoom.status !== 'waiting') {
        updatedRoom.winnerUserId = state.room.winnerUserId;
      }

      // Phase transitions based on room status
      let phase = state.phase;
      if (updatedRoom.status === 'playing' && state.phase === 'lobby') {
        phase = 'playing';
      } else if (updatedRoom.status === 'finished') {
        phase = 'results';
      } else if (updatedRoom.status === 'waiting' && state.phase === 'results') {
        phase = 'lobby';
      }

      // Reset timer when new round starts (answering)
      let timeRemaining = state.timeRemaining;
      if (action.room.roundStatus === 'answering' && state.room.roundStatus !== 'answering') {
        timeRemaining = ROUND_TIMER_MS;
      }

      return { ...state, phase, room: updatedRoom, timeRemaining };
    }

    case 'PLAYERS_UPDATED': {
      if (!state.room) return state;
      return { ...state, room: { ...state.room, players: action.players } };
    }

    case 'OPTIMISTIC_READY': {
      if (!state.room) return state;
      const players = state.room.players.map(p =>
        p.userId === action.userId ? { ...p, isReady: action.ready } : p
      );
      return { ...state, room: { ...state.room, players } };
    }

    case 'GAME_STARTED':
      return {
        ...state,
        phase: 'playing',
        room: action.room,
        timeRemaining: ROUND_TIMER_MS,
      };

    case 'NEW_ROUND':
      if (!state.room) return state;
      return {
        ...state,
        room: {
          ...state.room,
          currentQuestion: action.currentQuestion,
          currentQuestionIndex: action.questionIndex,
          roundStatus: 'answering',
          roundReveal: null,
          roundStartedAt: action.roundStartedAt,
        },
        timeRemaining: ROUND_TIMER_MS,
      };

    case 'PLAYER_ANSWERED': {
      if (!state.room) return state;
      const players = state.room.players.map((p) =>
        p.userId === action.userId ? { ...p, hasAnswered: true } : p
      );
      return { ...state, room: { ...state.room, players } };
    }

    case 'ROUND_REVEAL':
      if (!state.room) return state;
      return {
        ...state,
        room: { ...state.room, roundStatus: 'revealing', roundReveal: action.reveal },
      };

    case 'GAME_FINISHED':
      if (!state.room) return state;
      return {
        ...state,
        phase: 'results',
        room: { ...state.room, status: 'finished', winnerUserId: action.winnerUserId },
      };

    case 'TICK': {
      if (state.phase !== 'playing' || state.room?.roundStatus !== 'answering') return state;
      const newTime = Math.max(0, state.timeRemaining - action.delta);
      return { ...state, timeRemaining: newTime };
    }

    case 'BACK_TO_LOBBY':
      if (!state.room) return state;
      return {
        ...state,
        phase: 'lobby',
        room: {
          ...state.room,
          status: 'waiting',
          currentQuestionIndex: 0,
          currentQuestion: null,
          roundStatus: null,
          roundReveal: null,
          roundStartedAt: null,
          winnerUserId: null,
          players: state.room.players.map((p) => ({
            ...p,
            lives: 3,
            score: 0,
            hasAnswered: false,
            isEliminated: false,
            isReady: false,
          })),
        },
        timeRemaining: ROUND_TIMER_MS,
      };

    case 'RESET':
      return { ...initialState };

    default:
      return state;
  }
}

export function useBattleGame(roomCode: string | undefined) {
  const [state, dispatch] = useReducer(battleReducer, initialState);
  const { user } = useAuth();
  const navigate = useNavigate();
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const lastTickRef = useRef<number>(0);
  const timeoutSentRef = useRef(false);
  const hasAnsweredRef = useRef(false);
  const isStartingRef = useRef(false);

  // --- Timer ---

  const stopTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const startTimer = useCallback(() => {
    stopTimer();
    lastTickRef.current = Date.now();
    timerRef.current = setInterval(() => {
      const now = Date.now();
      const delta = now - lastTickRef.current;
      lastTickRef.current = now;
      dispatch({ type: 'TICK', delta });
    }, TICK_INTERVAL_MS);
  }, [stopTimer]);

  useEffect(() => () => stopTimer(), [stopTimer]);

  // Start/stop timer based on round status
  useEffect(() => {
    if (state.phase === 'playing' && state.room?.roundStatus === 'answering') {
      startTimer();
      timeoutSentRef.current = false;
      hasAnsweredRef.current = false;
    } else {
      stopTimer();
    }
  }, [state.phase, state.room?.roundStatus, startTimer, stopTimer]);

  // Handle client-side timeout
  useEffect(() => {
    if (
      state.timeRemaining <= 0 &&
      state.room?.roundStatus === 'answering' &&
      !timeoutSentRef.current &&
      !hasAnsweredRef.current &&
      roomCode
    ) {
      timeoutSentRef.current = true;
      post(`/api/trivia-rush/battle/${roomCode}/timeout`, {}).catch(() => {});
    }
  }, [state.timeRemaining, state.room?.roundStatus, roomCode]);

  // --- Realtime subscription ---

  const handleRoomUpdate = useCallback((payload: Partial<BattleRoomState>) => {
    dispatch({ type: 'ROOM_UPDATED', room: payload });
  }, []);

  const handlePlayersUpdate = useCallback((players: BattlePlayerState[]) => {
    dispatch({ type: 'PLAYERS_UPDATED', players });
  }, []);

  useBattleRoom({
    roomId: state.dbRoomId,
    onRoomUpdate: handleRoomUpdate,
    onPlayersUpdate: handlePlayersUpdate,
  });

  // --- Join room on mount ---

  useEffect(() => {
    if (!roomCode || !user) return;

    let cancelled = false;

    const joinRoom = async () => {
      dispatch({ type: 'SET_LOADING' });
      try {
        const response = await post<BattleRoomState>(`/api/trivia-rush/battle/${roomCode}/join`, {});
        if (cancelled) return;
        const room = response.data;
        dispatch({ type: 'JOINED_ROOM', room });

        // Get the DB room ID for Realtime subscription
        const { data: dbRoom } = await supabase
          .from('battle_rooms')
          .select('id')
          .eq('room_code', roomCode)
          .single();

        if (dbRoom && !cancelled) {
          dispatch({ type: 'SET_DB_ROOM_ID', dbRoomId: dbRoom.id });
        }
      } catch (err) {
        if (!cancelled) {
          dispatch({ type: 'SET_ERROR', error: getErrorMessage(err) });
        }
      }
    };

    joinRoom();
    return () => { cancelled = true; };
  }, [roomCode, user]);

  // --- Polling fallback (catches missed Realtime events in lobby + playing) ---

  useEffect(() => {
    if (state.phase !== 'lobby' && state.phase !== 'playing' && state.phase !== 'results') return;
    if (!roomCode) return;
    const intervalMs = state.phase === 'playing' ? 2000 : 3000;
    const interval = setInterval(async () => {
      try {
        const { data } = await get<BattleRoomState>(`/api/trivia-rush/battle/${roomCode}`);
        dispatch({ type: 'ROOM_UPDATED', room: data });
      } catch {}
    }, intervalMs);
    return () => clearInterval(interval);
  }, [state.phase, roomCode]);

  // --- Actions ---

  const createRoom = useCallback(async (): Promise<string | null> => {
    if (!user) return null;
    dispatch({ type: 'SET_LOADING' });
    try {
      const response = await post<{ roomCode: string; inviteLink: string }>('/api/trivia-rush/battle/create', {});
      return response.data.roomCode;
    } catch (err) {
      dispatch({ type: 'SET_ERROR', error: getErrorMessage(err) });
      return null;
    }
  }, [user]);

  const startGame = useCallback(async () => {
    if (!roomCode || isStartingRef.current) return;
    isStartingRef.current = true;
    try {
      await post(`/api/trivia-rush/battle/${roomCode}/start`, {});
    } catch (err) {
      dispatch({ type: 'SET_ERROR', error: getErrorMessage(err) });
    } finally {
      isStartingRef.current = false;
    }
  }, [roomCode]);

  const submitAnswer = useCallback(async (choice: 'A' | 'B') => {
    if (!roomCode || hasAnsweredRef.current) return;
    hasAnsweredRef.current = true;
    try {
      await post(`/api/trivia-rush/battle/${roomCode}/answer`, { choice });
    } catch (err) {
      hasAnsweredRef.current = false;
      dispatch({ type: 'SET_ERROR', error: getErrorMessage(err) });
    }
  }, [roomCode]);

  const toggleReady = useCallback(async (ready: boolean) => {
    if (!roomCode || !user) return;
    dispatch({ type: 'OPTIMISTIC_READY', userId: user.id, ready });
    try {
      await post(`/api/trivia-rush/battle/${roomCode}/ready`, { ready });
    } catch (err) {
      dispatch({ type: 'OPTIMISTIC_READY', userId: user.id, ready: !ready });
      dispatch({ type: 'SET_ERROR', error: getErrorMessage(err) });
    }
  }, [roomCode, user]);

  const leaveRoom = useCallback(async () => {
    if (!roomCode) return;
    try {
      await post(`/api/trivia-rush/battle/${roomCode}/leave`, {});
    } catch {}
    navigate('/trivia-rush');
  }, [roomCode, navigate]);

  const restartRoom = useCallback(async () => {
    if (!roomCode) return;
    try {
      await post(`/api/trivia-rush/battle/${roomCode}/restart`, {});
      dispatch({ type: 'BACK_TO_LOBBY' });
    } catch (err) {
      dispatch({ type: 'SET_ERROR', error: getErrorMessage(err) });
    }
  }, [roomCode]);

  // Derived state
  const currentPlayer = state.room?.players.find((p) => p.userId === user?.id) || null;
  const isHost = state.room?.hostUserId === user?.id;
  const myAnswer = currentPlayer?.hasAnswered || false;

  return {
    state,
    currentPlayer,
    isHost,
    myAnswer,
    createRoom,
    startGame,
    submitAnswer,
    leaveRoom,
    toggleReady,
    restartRoom,
  };
}
